import { useEffect, useRef, useState } from 'react';
import { isWelcomeVoiceOn, playServerGreeting, type PlayedGreeting } from '../lib/welcome-voice';
import { beginWelcome, welcomeOver } from '../lib/welcome-sequence';
import { aiAvailable } from '../lib/ai-availability';

/**
 * The assistant, in person, delivering the spoken welcome.
 *
 * She appears at the corner of the catalogue, speaks, and steps aside; the
 * chat panel opens as she goes. The whole point is the ordering — a portrait
 * that arrives with the voice and leaves when the voice stops reads as one
 * person introducing herself, where a picture, a voice and a dialog arriving
 * together read as three notifications.
 *
 * It replaces an earlier component that played the recording and rendered
 * nothing. Same recording, same rules — on by default, silenced from the
 * appearance menu, no synthesised fallback — with a face attached.
 *
 * Decisions worth keeping:
 *
 *   * **She leaves on the recording's own timing**, not on a duration
 *     written down here. Replacing the recording with a longer one must not
 *     mean editing a component.
 *   * **She is not shown at all if there is nothing to say.** Muted, no
 *     recording on the server, a browser with no audio: the catalogue opens
 *     as it always did and the panel is released at once.
 *   * **She never swallows a click.** The figure is `pointer-events: none` —
 *     she is an announcement standing in front of the page, and a visitor who
 *     wants to scroll past her should not have to wait for her.
 */

/** Matches the fade in styles.css; long enough to read as leaving, not blinking. */
const FADE_MS = 520;

/** How long she waits for "Tap to hear the welcome" before stepping aside. */
const UNTAPPED_MS = 45_000;

export function WelcomeAvatar() {
  /** Off screen, speaking, or on the way out. */
  const [phase, setPhase] = useState<'idle' | 'here' | 'leaving'>('idle');
  /**
   * The browser is holding the sound back until the visitor interacts — the
   * usual state on a first visit. She stands silent and asks for a tap,
   * rather than miming a greeting nobody can hear.
   */
  const [waiting, setWaiting] = useState(false);
  const greeting = useRef<PlayedGreeting | null>(null);

  useEffect(() => {
    // Claimed before anything is known, so the panel cannot open in the gap
    // while the recording is still being fetched. The token identifies this
    // run's claim: everything below releases its own claim and no one else's,
    // which matters because React abandons a run and starts another in
    // development while the first one's fetch is still in flight.
    const token = beginWelcome();

    if (!isWelcomeVoiceOn() || !window.Audio) {
      welcomeOver(token);
      return;
    }

    let cancelled = false;
    let stop: (() => void) | null = null;
    let timer = 0;
    let untapped = 0;

    void (async () => {
      // The greeting introduces the assistant by name, so with the assistant
      // switched off there is nothing true for it to say.
      if (!(await aiAvailable()) || cancelled) {
        welcomeOver(token);
        return;
      }

      // Played only when the visitor taps "Tap to hear the welcome" — never
      // on arrival, even where the browser would allow it.
      const played = await playServerGreeting(undefined, { waitForTap: true });

      // No recording on the server, or the fetch failed. Nothing to show,
      // and nothing for the panel to wait behind.
      if (!played || cancelled) {
        played?.stop();
        welcomeOver(token);
        return;
      }

      stop = played.stop;
      greeting.current = played;
      setPhase('here');
      if (played.deferred) {
        setWaiting(true);
        // The chat is hidden while she stands here, so she cannot wait for
        // ever: unanswered, she steps aside quietly and the chat opens.
        untapped = window.setTimeout(() => played.stop(), UNTAPPED_MS);
        void played.started.then(() => {
          window.clearTimeout(untapped);
          if (!cancelled) setWaiting(false);
        });
      }

      // Resolves when the recording ends, fails, or is stopped. If the
      // browser deferred it for want of a gesture she waits here, on screen,
      // until the visitor touches the page — the right place to wait, since
      // she is the thing inviting the touch.
      await played.finished;
      if (cancelled) return;

      // She starts leaving and the panel starts opening together, so it
      // arrives in the space she is vacating rather than after a pause.
      setWaiting(false);
      setPhase('leaving');
      welcomeOver(token);
      timer = window.setTimeout(() => setPhase('idle'), FADE_MS);
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.clearTimeout(untapped);
      stop?.();
      // Leaving the catalogue mid-welcome must not strand the panel behind a
      // signal that is never coming.
      welcomeOver(token);
    };
  }, []);

  if (phase === 'idle') return null;

  return (
    <div
      className={`welcomer${phase === 'leaving' ? ' welcomer--out' : ''}${waiting ? ' welcomer--waiting' : ''}`}
    >
      {waiting ? (
        // The one interactive part of her. Any tap or key on the page also
        // starts the sound; this says so, and gives a way out.
        <div className="welcomer__ask" role="group" aria-label="Welcome message">
          <button
            type="button"
            className="welcomer__play"
            onClick={() => void greeting.current?.start()}
          >
            <span aria-hidden="true">🔊</span> Tap to hear the welcome
          </button>
          <button
            type="button"
            className="welcomer__skip"
            data-welcome-skip=""
            onClick={() => greeting.current?.stop()}
          >
            Skip
          </button>
        </div>
      ) : null}
      {/* Decorative. The greeting is spoken, and the same words are written in
          the panel that follows her, so there is nothing here a screen reader
          will not be given properly a moment later. */}
      {/* Four layers on one canvas, so she can speak and gesture. The body
          carries everything except her mouth and her sleeves; those are
          separate so the mouth can open and the sleeves can turn at the
          shoulder. All four are the same size and stack on top of each other,
          which is why the pivots can be fixed percentages — see the welcomer
          block in styles.css. */}
      <div className="welcomer__rig" aria-hidden="true">
        <img
          className="welcomer__layer"
          src="/brand/ai-assistant-body.webp"
          alt=""
          width={304}
          height={612}
          decoding="async"
        />
        <img
          className="welcomer__layer welcomer__mouth"
          src="/brand/ai-assistant-mouth.webp"
          alt=""
          width={304}
          height={612}
          decoding="async"
        />
        <img
          className="welcomer__layer welcomer__arm welcomer__arm--l"
          src="/brand/ai-assistant-arm-l.webp"
          alt=""
          width={304}
          height={612}
          decoding="async"
        />
        <img
          className="welcomer__layer welcomer__arm welcomer__arm--r"
          src="/brand/ai-assistant-arm-r.webp"
          alt=""
          width={304}
          height={612}
          decoding="async"
        />
      </div>
    </div>
  );
}
